import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import MultiSelect from '../ui/MultiSelect';
import Button from '../ui/Button';
import { listWorkflowCategories } from '../../api/workflow';

export const VEHICLE_OPTIONS = [
  { value: 'taxi', label: 'Taxi' },
  { value: 'bike', label: 'Bike' },
];
export const LOCATION_OPTIONS = [
  { value: 'outside_uae', label: 'Outside UAE' },
  { value: 'inside_uae', label: 'Inside UAE' },
];
export const LICENSE_OPTIONS = [
  { value: 'uae', label: 'UAE License' },
  { value: 'gcc_home', label: 'GCC / Home License' },
];

/**
 * State for the shared multi-select filter set (vehicle type, location, license type, workflow,
 * step). Vehicle/location/license narrow the workflow list; workflows can be picked explicitly or
 * left empty to take every workflow matching the attributes.
 *
 * Returns:
 *  - `params`      query params to send to list endpoints (arrays; empty ones are omitted)
 *  - `categoryIds` the resolved workflow ids ([] = no workflow constraint)
 *  - `active`      true when any filter is set
 *  - `barProps`    spread into <CandidateFilterBar />
 */
export function useCandidateFilters({ withSteps = true } = {}) {
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [licenseTypes, setLicenseTypes] = useState([]);
  const [pickedIds, setPickedIds] = useState([]);
  const [stepIds, setStepIds] = useState([]);

  const categoriesQuery = useQuery({ queryKey: ['workflow-categories'], queryFn: listWorkflowCategories });
  const allCategories = useMemo(() => categoriesQuery.data || [], [categoriesQuery.data]);

  const matching = useMemo(
    () =>
      allCategories.filter(
        (c) =>
          (!vehicleTypes.length || vehicleTypes.includes(c.vehicle_type)) &&
          (!locations.length || locations.includes(c.location)) &&
          (!licenseTypes.length || licenseTypes.includes(c.license_type))
      ),
    [allCategories, vehicleTypes, locations, licenseTypes]
  );
  const hasAttributeFilter = vehicleTypes.length > 0 || locations.length > 0 || licenseTypes.length > 0;

  const selectedCategories = useMemo(() => {
    const picked = matching.filter((c) => pickedIds.includes(String(c.id)));
    if (picked.length) return picked;
    return hasAttributeFilter ? matching : [];
  }, [matching, pickedIds, hasAttributeFilter]);
  const categoryIds = useMemo(() => selectedCategories.map((c) => String(c.id)), [selectedCategories]);

  // Drop picks / steps that fell out of the narrowed workflow list.
  useEffect(() => {
    const keep = new Set(matching.map((c) => String(c.id)));
    setPickedIds((prev) => (prev.every((id) => keep.has(id)) ? prev : prev.filter((id) => keep.has(id))));
  }, [matching]);
  useEffect(() => {
    const keep = new Set(selectedCategories.flatMap((c) => (c.steps || []).map((st) => String(st.id))));
    setStepIds((prev) => (prev.every((id) => keep.has(id)) ? prev : prev.filter((id) => keep.has(id))));
  }, [selectedCategories]);

  const stepOptions = useMemo(
    () =>
      selectedCategories.flatMap((c) =>
        (c.steps || []).map((s) => ({
          value: String(s.id),
          label: selectedCategories.length > 1 ? `${c.name} — ${s.title}` : s.title,
        }))
      ),
    [selectedCategories]
  );

  const active = hasAttributeFilter || pickedIds.length > 0 || stepIds.length > 0;
  const reset = () => {
    setVehicleTypes([]);
    setLocations([]);
    setLicenseTypes([]);
    setPickedIds([]);
    setStepIds([]);
  };

  // Stable identity so it can sit in query keys / effect deps.
  const params = useMemo(() => {
    const p = {};
    if (vehicleTypes.length) p.vehicle_types = vehicleTypes;
    if (locations.length) p.locations = locations;
    if (licenseTypes.length) p.license_types = licenseTypes;
    if (pickedIds.length) p.workflow_category_ids = pickedIds;
    if (withSteps && stepIds.length) p.workflow_step_ids = stepIds;
    return p;
  }, [vehicleTypes, locations, licenseTypes, pickedIds, stepIds, withSteps]);

  return {
    params,
    categoryIds,
    stepIds,
    active,
    reset,
    barProps: {
      vehicleTypes, setVehicleTypes,
      locations, setLocations,
      licenseTypes, setLicenseTypes,
      pickedIds, setPickedIds,
      stepIds, setStepIds,
      matching, hasAttributeFilter, stepOptions,
      categoryIds, withSteps, active, reset,
    },
  };
}

export default function CandidateFilterBar({
  vehicleTypes, setVehicleTypes,
  locations, setLocations,
  licenseTypes, setLicenseTypes,
  pickedIds, setPickedIds,
  stepIds, setStepIds,
  matching, hasAttributeFilter, stepOptions,
  categoryIds, withSteps, active, reset,
  children,
  className = 'mb-4',
}) {
  return (
    <div className={`flex flex-wrap items-start gap-3 ${className}`}>
      <MultiSelect placeholder="Vehicle type" options={VEHICLE_OPTIONS} value={vehicleTypes} onChange={setVehicleTypes} className="w-44" />
      <MultiSelect placeholder="Location" options={LOCATION_OPTIONS} value={locations} onChange={setLocations} className="w-44" />
      <MultiSelect placeholder="License type" options={LICENSE_OPTIONS} value={licenseTypes} onChange={setLicenseTypes} className="w-44" />
      <MultiSelect
        placeholder={hasAttributeFilter ? 'All matching workflows' : 'All workflows'}
        options={matching.map((c) => ({ value: String(c.id), label: c.name }))}
        value={pickedIds}
        onChange={setPickedIds}
        className="w-64"
      />
      {withSteps && (
        <MultiSelect
          placeholder={categoryIds.length ? 'Any step' : 'Any step (pick a workflow first)'}
          options={stepOptions}
          value={stepIds}
          onChange={setStepIds}
          disabled={categoryIds.length === 0}
          className="w-72"
        />
      )}
      {children}
      {active && (
        <Button variant="secondary" className="px-3 py-2 text-xs" onClick={reset}>
          Clear filters
        </Button>
      )}
    </div>
  );
}
